import { isEqual, isNull, isUndefined, noop, set } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch } from '../../../hooks/react-redux';
import { CalculatorField } from '../../admin/calculator/store';
import { fieldBoxShadow } from '../../constants/style';
import { DeleteButton } from '../../shared/button/DeleteButton';
import { DuplicateButton } from '../../shared/button/DuplicateButton';
import { IconButton } from '../../shared/button/IconButton';
import { PlusButton } from '../../shared/button/PlusButton';
import { newCalculatorField } from '../../shared/calculator/constants';
import { Calculator } from '../../shared/icons';
import { Number } from '../../shared/number/Number';
import { TooltipList } from '../../shared/tooltip';
import { updateFieldValue, userCorrectAIFieldValue, Valuation } from '../instances/store';
import { CalculatorModal } from './CalculatorModal';
import styles from './Fields.module.scss';
import { ReadOnlyCalculatorField } from './ReadOnlyCalculatorField';

const { disabledGrey, amber, gold, amethyst, green, primary } = styles;

interface ValuationPercentageProps {
    id: string;
    value: Valuation[];
    index: number;
    sectionId?: string;
    groupIndex?: number;
    rowId?: string;
    disabled?: boolean;
    placeholder?: string;
    showFieldUpdated?: boolean;
    showDatasetUpdated?: boolean;
    showAIModified?: boolean;
    showAIModifiedUserCorrected?: boolean;
    isMLCorrection?: boolean;
    showClause?: boolean;
    isTable?: boolean;
    includedInAnnex?: boolean;
    datasetId: number;
    parentFieldId: string;
    modalInstance?: boolean;
    showValuationPercentageMargin?: boolean;
}

export const ValuationPercentage: React.FC<ValuationPercentageProps> = ({
    id,
    value,
    disabled = false,
    index,
    sectionId,
    groupIndex,
    rowId,
    showFieldUpdated = false,
    showDatasetUpdated = false,
    showAIModified = false,
    showAIModifiedUserCorrected = false,
    isMLCorrection = false,
    datasetId,
    parentFieldId,
    modalInstance,
    showValuationPercentageMargin = false
}) => {
    const [calculatorModalIndex, setCalculatorModalIndex] = useState<number | undefined>(undefined);
    const [calculatorInstance, setCalculatorInstance] = useState<CalculatorField[] | null>(null);

    const dispatch = useAppDispatch();
    const updateValue = useCallback((value: Valuation[]) => {
        if (isMLCorrection) {
            dispatch(userCorrectAIFieldValue(datasetId, parentFieldId, value, index, sectionId, groupIndex, rowId));
        } else {
            dispatch(updateFieldValue(datasetId, parentFieldId, value, index, sectionId, groupIndex, rowId, modalInstance));
        }
    }, [dispatch, datasetId, parentFieldId, index, sectionId, groupIndex, rowId, modalInstance, isMLCorrection]);

    const updateRowPercentage = useCallback((percentage: number | undefined, rowIndex: number) => {
        const updatedValue = value.map((valuation, index) => index === rowIndex ? set('percentage', percentage, valuation) : valuation);
        updateValue(updatedValue);
    }, [updateValue, value]);

    const updateRowMaturity = useCallback((maturity: CalculatorField[], rowIndex: number) => {
        const updatedValue = value.map((valuation, index) => index === rowIndex ? set('maturity', maturity, valuation) : valuation);
        updateValue(updatedValue);
    }, [updateValue, value]);

    const duplicateRow = useCallback((rowIndex: number) => {
        const duplicatedRow = value[rowIndex];
        updateValue([...value, duplicatedRow]);
    }, [updateValue, value]);

    const addRow = useCallback(() => {
        const updatedValue = [...value, { percentage: 0, maturity: [newCalculatorField()] }];
        updateValue(updatedValue);
    }, [updateValue, value]);

    const removeRow = useCallback((rowIndex: number) => {
        const updatedValue = value.filter((_, index) => index !== rowIndex);
        updateValue(updatedValue);
    }, [updateValue, value]);

    const borderColor = useMemo(() => {
        if (!showDatasetUpdated && !showFieldUpdated && !showAIModified && !showAIModifiedUserCorrected) {
            return disabled ? disabledGrey : primary;
        }
        if (showAIModifiedUserCorrected) {
            return amber;
        }
        if (showAIModified) {
            return gold;
        }
        return showFieldUpdated ? amethyst : green;
    }, [showDatasetUpdated, showFieldUpdated, showAIModified, disabled, showAIModifiedUserCorrected]);

    const boxShadow = useMemo(() => [amethyst, green, amber, gold].includes(borderColor) ? fieldBoxShadow(borderColor) : undefined, [borderColor]);

    const openModal = useCallback((index: number) => {
        setCalculatorModalIndex(index);
        setCalculatorInstance(value[index].maturity);
    }, [value]);

    const closeModal = useCallback(() => {
        setCalculatorModalIndex(undefined);
        setCalculatorInstance(null);
    }, []);

    const closeAndUpdateCalculatorCell = useCallback(() => {
        if (!isUndefined(calculatorModalIndex) && !isNull(calculatorInstance)) {
            updateRowMaturity(calculatorInstance, calculatorModalIndex);
        }
        closeModal();
    }, [calculatorModalIndex, calculatorInstance, closeModal, updateRowMaturity]);

    const valuationPercentageHeader = useMemo(() => showValuationPercentageMargin ? 'VP / Margin' : 'Valuation Percentage', [showValuationPercentageMargin]);
    const valuationPercentageTooltip = useMemo(() => showValuationPercentageMargin ? ['If the value is less than 100%, this should be considered a Valuation Percentage (100% - Haircut)', 'If the value is greater than 100% this should be considered as a Margin'] : null, [showValuationPercentageMargin]);

    if (disabled && value.length === 1 && isEqual(value[0].maturity, [newCalculatorField()])) {
        return (
            <div className={styles.valuationPercentageWrapper}>
                <Number onChange={noop} value={value[0].percentage} controlBorderColor={borderColor} disabled={disabled} allowDecimal />
            </div>
        );
    }

    return (
        <div className={styles.valuationPercentageWrapper}>
            <div className={styles.valuationPercentageHeader}>
                <div className={styles.maturityHeaderCell}>Maturity</div>
                <TooltipList overlayText={valuationPercentageTooltip}>
                    <div className={styles.percentageHeaderCell}>{valuationPercentageHeader}</div>
                </TooltipList>
                <div className={styles.actionHeaderCell} />
                <div className={styles.actionHeaderCell} />
            </div>
            <div className={styles.valuationPercentageTable}>
                {value.map(({ maturity, percentage }, index) => (
                    <div className={styles.valuationPercentageRow} key={index}>
                        <div className={styles.maturityFieldWrapper}>
                            <div className={styles.readOnlyCalculatorField} style={{ width: `${disabled ? '100%' : 'calc(100% - 20px)'}` }}>
                                {maturity.map(({ calculation, calculationCondition, output, gracePeriod }, calculationIndex) => (
                                    <ReadOnlyCalculatorField
                                        value={maturity}
                                        calculation={calculation}
                                        output={output}
                                        gracePeriod={gracePeriod}
                                        calculationCondition={calculationCondition}
                                        calculationIndex={calculationIndex}
                                        borderColor='transparent'
                                        key={calculationIndex}
                                        showOutputField={false}
                                        showGracePeriod={false}
                                    />
                                ))}
                            </div>
                            {!disabled &&
                                <div className={styles.editMaturityButton}>
                                    <IconButton icon={Calculator} disabled={disabled} onClick={() => openModal(index)} fontSize={20} />
                                </div>
                            }
                        </div>
                        <div className={styles.percentageWrapper}>
                            <Number onChange={val => updateRowPercentage(val, index)} value={percentage} controlBorderColor={borderColor} boxShadow={boxShadow} disabled={disabled} allowDecimal />
                        </div>
                        <div className={styles.actionRowCell}>
                            {!disabled && <DuplicateButton onClick={() => duplicateRow(index)} fontSize={18} />}
                        </div>
                        <div className={styles.actionRowCell}>
                            {value.length > 1 && !disabled && <DeleteButton onClick={() => removeRow(index)} fontSize={18} />}
                        </div>
                    </div>
                ))}
                {!disabled &&
                    <div className={styles.addTableRow}>
                        <PlusButton onClick={addRow} fontSize={16} />
                    </div>
                }

            </div>
            {!isUndefined(calculatorModalIndex) && !isNull(calculatorInstance) &&
                <CalculatorModal
                    isOpen
                    disabled={disabled}
                    closeModal={closeModal}
                    fieldType='maturity'
                    value={calculatorInstance}
                    updateValue={setCalculatorInstance}
                    borderColor={borderColor}
                    id={`${id}-${calculatorModalIndex}`}
                    saveModal={closeAndUpdateCalculatorCell}
                    showOutputField={false}
                    showGracePeriod={false}
                    includeFreeTextOption={false}
                />
            }
        </div>
    );
};
